import { IAzureSearchOptions, IFilterSet, IFilterSetItem, instanceOfSampleSize } from '@vivli/features/search/infrastructure/interface';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { instanceOfDateRange } from '@vivli/shared/infrastructure/interface';
import moment from 'moment';
import { AssignedAppTypeEnum } from '@vivli/shared/infrastructure/enum';
import { FilterOperatorEnum } from '@vivli/features/search/infrastructure/enum';
import { useAzureSearchApi } from './use-azure-search-api.hook';
import { IAzureSearch, IAzureSearchApiOptions } from '@vivli/core/infrastructure/interface';
import { useAzureSearchOptions } from './use-azure-search-options.hook';

export const useAzureSearch = ({ quoteSearchTerms = true, ...props }: IAzureSearchApiOptions): IAzureSearch => {
    const { apiOptions } = useAzureSearchOptions(props);
    const searchApi = useAzureSearchApi({ ...props, options: apiOptions });

    const addToFilterString = (filterString: string, stringToAdd: string): string => {
        if (!stringToAdd || stringToAdd.length <= 0) {
            return filterString;
        }

        if (filterString.trim().length > 0) {
            return `${filterString} and (${stringToAdd})`;
        }

        return stringToAdd;
    };

    const parseAnySyntaxItem = (item: IFilterSetItem, key: string): string => {
        return `${key}/any(l: l eq '${item.value}')`;
    };

    const parseDefaultItem = (item: IFilterSetItem, key: string): string => {
        if (typeof item.value === 'boolean') {
            return `${key} eq ${item.value}`;
        }

        return `${key} eq '${item.value}'`;
    };

    const parseRangeItem = (from: any, to: any, key: string): string => {
        if (!from && !to) {
            return '';
        }

        let result = '';

        const hasFromValue = from !== null && from !== undefined;
        const hasToValue = to !== null && to !== undefined;
        const useAndOperator = hasFromValue && hasToValue;

        if (hasFromValue) {
            result += `${key} ge ${from}`;
        }

        if (useAndOperator) {
            result += ' and ';
        }

        if (hasToValue) {
            result += `${key} le ${to}`;
        }

        return result;
    };

    const enumerateYearsBetweenDates = (startDate: Date, endDate: Date): number[] => {
        const now = moment(startDate);
        const dates: number[] = [];

        while (now.isSameOrBefore(moment(endDate))) {
            dates.push(now.year());
            now.add(1, 'years');
        }

        return dates;
    };

    const getYearRange = (from?: Date, to?: Date): number[] => {
        const fromYear = from?.getFullYear();
        const toYear = to?.getFullYear();

        if (fromYear === toYear) {
            return [toYear];
        } else if (fromYear && !toYear) {
            return [fromYear];
        } else if (toYear && !fromYear) {
            return [toYear];
        } else {
            return enumerateYearsBetweenDates(from, to);
        }
    };

    const parseDefaultDateRange = (item: IFilterSetItem, key: string): string => {
        if (!instanceOfDateRange(item.value)) {
            return '';
        }

        const from = item.value.from?.toISOString() || null;
        const to = item.value.to?.toISOString() || null;
        return parseRangeItem(from, to, key);
    };

    const parseYearDateRange = (item: IFilterSetItem, key: string): string => {
        if (!instanceOfDateRange(item.value) || !item.value.from || !item.value.to) {
            return '';
        }

        const yearRange = getYearRange(item.value.from, item.value.to);

        let result = '';
        if (yearRange.length === 0) {
            result += `${key}/any(l: l eq '9999')`;
        }

        yearRange.forEach((year, i) => {
            if (i > 0 && result.length > 0) {
                result += ' or ';
            }

            result += `${key}/any(l: l eq '${year}')`;
        });

        return result;
    };

    const parseDateRangeItem = (item: IFilterSetItem, key: string, isYearRange?: boolean): string => {
        if (!instanceOfDateRange(item.value)) {
            return '';
        }

        if (isYearRange) {
            return parseYearDateRange(item, key);
        } else {
            return parseDefaultDateRange(item, key);
        }
    };

    const parseSampleSizeItem = (item: IFilterSetItem, key: string): string => {
        if (!instanceOfSampleSize(item.value)) {
            return '';
        }

        const from = item.value.min;
        const to = item.value.max;
        return parseRangeItem(from, to, key);
    };

    const parseFilterSetItems = (filterSet: IFilterSet): string => {
        let result = '';

        filterSet.items.forEach((item, i) => {
            const isSampleSize = instanceOfSampleSize(item.value);
            const isDateRange = instanceOfDateRange(item.value);

            const operator = ` ${filterSet.itemOperator} `;

            let parsedItem = '';
            if (filterSet.operator === FilterOperatorEnum.Any) {
                parsedItem = parseAnySyntaxItem(item, item.key || filterSet.key);
            } else if (isDateRange) {
                parsedItem = parseDateRangeItem(item, item.key || filterSet.key, filterSet.isYearRange);
            } else if (isSampleSize) {
                parsedItem = parseSampleSizeItem(item, item.key || filterSet.key);
            } else {
                parsedItem = parseDefaultItem(item, item.key || filterSet.key);
            }

            if (parsedItem && parsedItem.length > 0) {
                if (i > 0 && result.length > 0) {
                    result += operator;
                }

                result += parsedItem;
            }
        });

        if (result.length > 0) {
            return `(${result})`;
        }

        return result;
    };

    const parseFilterSets = (filterSets: IFilterSet[]): string => {
        let result = '';

        filterSets.forEach((set, i) => {
            const parsedFilterItemsStr = parseFilterSetItems(set);

            if (parsedFilterItemsStr?.length > 0) {
                if (i > 0 && result.length > 0) {
                    result += ' and ';
                }

                result += parsedFilterItemsStr;
            }
        });

        return result;
    };

    const parseSelectString = (select: string[]): string => {
        let selectString = '';

        select.forEach((field, i) => {
            selectString += field;
            if (i !== select.length - 1) {
                selectString += ',';
            }
        });

        return selectString;
    };

    const isQuotedString = (str: string): boolean => {
        const lastChar = str.charAt(str.length - 1);
        const firstChar = str.charAt(0);

        return lastChar === '"' && firstChar === '"';
    };

    const parseWord = (word: string): string => {
        let result = word;

        if (result === '*') {
            return result;
        }

        if (word === '-') {
            result = 'not';
        }

        if (word === '/' || word === '&') {
            result = 'and';
        }

        return quoteSearchTerms ? `"${result}"` : result;
    };

    const parseSearchString = (searchString: string): string => {
        return decodeURIComponent(
            searchString
                ?.toLowerCase()
                .split(/(".*?")/g)
                .filter((x) => x.length > 0)
                .reduce((acc, value) => {
                    const isQuoted = isQuotedString(value);

                    if (isQuoted) {
                        acc = [...acc, value];
                    } else {
                        const words = value
                            .trim()
                            .split(' ')
                            .map((word) => parseWord(word));

                        acc = [...acc, ...words];
                    }

                    return acc;
                }, [])
                .join(' ')
                .replace(/ "and" /g, ' + ')
                .replace(/ "not" /g, ' - ')
                .replace(/ "or" /g, ' | ')
        );
    };

    const getSearchCount = (assignedAppType?: AssignedAppTypeEnum): Observable<number> => {
        const filter = assignedAppType ? `assignedAppType eq '${assignedAppType}'` : '';

        return searchApi
            .getDocuments({
                filter,
                top: 0,
                includeTotalCount: true,
                search: '*',
            })
            .pipe(map((result) => result['@odata.count']));
    };

    const getDocument = <T>(documentId: string, searchOptions?: IAzureSearchOptions) => {
        const selectString = searchOptions?.select ? parseSelectString(searchOptions.select) : null;

        return searchApi.getDocument<T>(documentId, {
            filter: searchOptions?.filter,
            includeTotalCount: true,
            skip: searchOptions?.skip,
            top: searchOptions?.top,
            search: searchOptions?.searchText,
            select: selectString,
            orderBy: searchOptions?.orderBy,
        });
    };

    const search = <DocumentType, FacetType = void>(
        searchOptions: IAzureSearchOptions
    ): Observable<{ result: DocumentType[]; count: number; facets: FacetType }> => {
        const { assignedAppType, filterSets, searchText, top, skip, filter, select, orderBy, facets } = {
            ...searchOptions,
            ...searchOptions,
        };

        let filterString = '';

        if (assignedAppType) {
            filterString = addToFilterString(filterString, `assignedAppType eq '${assignedAppType}'`);
        }

        if (filterSets?.length > 0) {
            filterString = addToFilterString(filterString, parseFilterSets(filterSets));
        }

        if (filter && filter.length > 0) {
            filterString = addToFilterString(filterString, filter);
        }

        const selectString = select ? parseSelectString(select) : null;

        const parsedSearchString = parseSearchString(searchText);

        return searchApi
            .getDocuments({
                filter: filterString,
                includeTotalCount: true,
                skip,
                top,
                search: parsedSearchString,
                select: selectString,
                orderBy,
                facets,
            })
            .pipe(
                map((result) => ({
                    count: result['@odata.count'],
                    result: result['value'] as DocumentType[],
                    facets: result['@search.facets'] as FacetType,
                }))
            );
    };

    return {
        ...searchApi,
        getSearchCount,
        getDocument,
        search,
    };
};
